const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/components/AppBar/AppBar.tsx';
import * as React from 'react';
import { useState } from 'react';
import { MessageDescriptor } from 'react-intl';
import { IconBtn } from './IconBtn';

import { TextArea } from './TextArea/TextArea';
import { css, cx } from '@emotion/css';
import { theme } from 'App/utils/clientSetupManager';
import { LogoutBtn } from '../Buttons/LogoutBtn';
import { isDarkColor } from '@cistec/common_web/utils/colorUtils';
import { BackToStartBtn } from '../Buttons/BackToStartBtn';
import { HttpPrefix } from 'App/defs/setupDefs';
import { CardActionAreaIconHeader } from '../Card/CardActionAreaIconHeader';
import { AppBar as MuiAppBar, Toolbar } from '@mui/material';
import { colors } from '@cistec/common_web/defs/colors';
import { DialogsAppBar } from './DialogAppBar';
import { useTheme } from '@mui/material/styles';
import useBreakpoint from '@cistec/common_web/hooks/useBreakpoint';
import { useUserLinked } from 'App/store/userLinkage';

/**
 * React props
 */
export interface IAppBarProps {
    /**
     * Specifies where the name of the patient should appear.
     * Defaults to 'never'.
     */
    patNameKind?: 'primary' | 'secondary' | 'never';

    /**
     * The primary AppBar title
     */
    primaryText?: string | MessageDescriptor;

    /**
     * URL that links to hospitalPage which is clickable on icon and primaryText
     */
    hospitalLink?: HttpPrefix;

    /**
     * The secondary AppBar secondary text:
     */
    secondaryText?: string | MessageDescriptor;

    /**
     * Tells the AppBar to position itself absolutely. Use this when controlling
     * the AppBar's height.
     */
    overlapsContent?: boolean;

    /**
     * The desired height of the AppBar, in pixels. Not needed unless you want to
     * control the AppBar's height dynamically. Note that the AppBar's minHeight
     * cannot be underrun with this.
     */
    desiredHeight?: number;

    /**
     * The maximum height of the AppBar. Not needed unless you want to control the
     * AppBar's height dynamically.
     */
    maxHeight?: number;

    /**
     * Url for a background image, if desired.
     */
    bgndImageUrl?: string;

    /**
     * or background color value.
     */
    bgndColor?: string;

    /**
     * ...and then text-color.
     */
    textColor?: string;

    /**
     * Specifies what kind of button should appear at the left-hand side.
     * Defaults to 'none'.
     */
    leftBtnKind?: 'menu' | 'back' | 'cross' | 'none';

    /**
     * Called when the back button is clicked. Implementations can return true here
     * to prevent the button's default behaviour.
     */
    backBtnClicked?: () => boolean;

    /**
     * Called when the menu button is clicked. Implementations can return true here
     * to prevent the button's default behaviour.
     */
    menuBtnClicked?: () => boolean;
    /**
     * AppBar contains logout btn
     */
    showLogoutBtn?: boolean;
    /**
     * AppBar contains Profile btn
     */
    showProfileBtn?: boolean;
    /**
     * AppBar contains Search btn
     */
    showSearchBtn?: boolean;
    /**
     * AppBar contains back to start btn
     */
    showBackToStartBtn?: boolean;

    showNotificationBtn?: boolean;
    children?: any;
    /**
     * progression in percent
     */
    progress?: number;
    logoElement?: React.ReactElement<any> | null;
    className?: string;

    hideFirstText?: boolean;
}

const prgBar = css`
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    z-index: -1;
`;

const pgrBarNotDone = css`
    background-color: ${theme?.custom?.progressNotDone};
    width: 100%;
`;
const pgrBarDone = css`
    background-color: ${theme.palette.primary.main};
    transition: width 1s;
`;
const barZIndex = css`
    z-index: 0;
`;
const textZIndex = css`
    z-index: 0;
`;

const textPositioningSmallDevices = css`
    left: ${theme.spacing(48 / 8)};
    position: absolute;
`;

const layoutStyle = css`
    display: flex;
    align-items: center;
`;

const claimStyle = css`
    ${theme?.breakpoints?.down?.('sm')} {
        display: none;
    }
`;

const alignbuttonsRight = css`
    margin-left: auto;
`;

const width100Percent = css`
    width: 100%;
`;

/**
 * React component
 * NOTE: This is *not* a functional component because of performance issues when used
 * with a variable height.
 */
// Removed some smartphone related appbar height with commit-sha 282e8dc9a683be552e58627d79ecba655377d5c8
export const AppBar = (props: IAppBarProps) => {
    const theme = useTheme();
    const isSmallDevice = useBreakpoint(theme?.breakpoints?.values?.sm);

    const [minHeight, setMinHeight] = useState(0);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const { data: isPatientLinkageAvailable } = useUserLinked();

    const hasLeftBtn = !!props.leftBtnKind && props.leftBtnKind !== 'none';
    const hasLogoutBtn = props.showLogoutBtn;
    const hasProfileButton = props.showProfileBtn;
    const hasSearchBtn = props.showSearchBtn;
    const hasBackToStartBtn = props.showBackToStartBtn;
    const needContrastColor = props.bgndColor && isDarkColor(props.bgndColor);
    const appBarInlineStyle: React.CSSProperties = {
        position: !props.overlapsContent ? undefined : 'absolute',
        left: !props.overlapsContent ? undefined : 0,
        right: !props.overlapsContent ? undefined : 0,
        backgroundColor: props.bgndColor ? props.bgndColor : undefined,
        color: needContrastColor ? 'white' : props.textColor ? props.textColor : undefined,
        zIndex: 1,
    };
    const toolbarInlineStyle: React.CSSProperties = {
        paddingLeft: hasLeftBtn ? 4 : undefined,
        paddingRight: !!props.children ? 4 : undefined,
    };

    const [dialogOpen, setDialogOpen] = useState(false);

    const getProgress = () => {
        if (props?.progress && props.progress > 100) {
            return 100;
        } else if (props?.progress && props.progress > 0 && props.progress <= 100) {
            return props.progress;
        } else {
            return 0;
        }
    };

    const progressSafe = getProgress();
    const progressLineInlineStyleLeft: React.CSSProperties = {
        width: `${progressSafe}%`,
    };

    const hasPrgBar = isSmallDevice && props.progress !== undefined;

    const toggleDrawer = () => {
        setIsDrawerOpen(!isDrawerOpen);
    };

    const toolbarRefFn = (e: HTMLElement | null) => {
        if (e) {
            // Mui uses CSS media queries to set the Toolbar's minHeight.
            // To access the value, we grab it from the computed styles.
            const h = Number.parseInt(window.getComputedStyle(e).getPropertyValue('min-height'), 10);
            setMinHeight(h);
        }
    };

    return (
        <MuiAppBar color="primary" position="relative" style={appBarInlineStyle}>
            <DialogsAppBar
                toggleDrawer={toggleDrawer}
                isDrawerOpen={isDrawerOpen}
                dialogOpen={dialogOpen}
                setDialogOpen={setDialogOpen}
            />
            <div className={layoutStyle}>
                <div className={cx(layoutStyle, hasPrgBar && width100Percent)}>
                    <CardActionAreaIconHeader>
                        {props.logoElement}
                        <Toolbar style={toolbarInlineStyle} ref={toolbarRefFn} className={width100Percent}>
                            {hasPrgBar && (
                                <div className={barZIndex}>
                                    <div className={cx(prgBar, pgrBarNotDone)}></div>
                                    <div className={cx(prgBar, pgrBarDone)} style={progressLineInlineStyleLeft}></div>
                                </div>
                            )}
                            {hasLeftBtn && (
                                <IconBtn
                                    kind={props.leftBtnKind}
                                    backBtnClicked={props.backBtnClicked}
                                    menuBtnClicked={props.menuBtnClicked}
                                    marginTop={!props.desiredHeight || !minHeight ? undefined : (minHeight - 48) / 2}
                                />
                            )}

                            {!props.hideFirstText && (
                                <div className={cx([textZIndex, claimStyle])}>
                                    <TextArea
                                        patNameKind={props.patNameKind}
                                        primaryText={props.primaryText}
                                        secondaryText={props.secondaryText}
                                        headerElement="h1"
                                    />
                                    {props.children}
                                </div>
                            )}
                        </Toolbar>
                    </CardActionAreaIconHeader>
                    {!hasLeftBtn ? (
                        <div className={cx(textZIndex, claimStyle)}>
                            <TextArea
                                patNameKind={props.patNameKind}
                                secondaryText={props.secondaryText}
                                headerElement="h1"
                            />
                            {props.children}
                        </div>
                    ) : (
                        <div className={cx(textZIndex, isSmallDevice ? textPositioningSmallDevices : undefined)}>
                            <TextArea
                                patNameKind={props.patNameKind}
                                primaryText={props.primaryText}
                                secondaryText={props.secondaryText}
                                primaryTextStyle={{ color: colors.teal['100'] }}
                                headerElement="h1"
                            />
                            {props.children}
                        </div>
                    )}
                </div>
                <div className={alignbuttonsRight}>
                    {hasSearchBtn && <IconBtn kind={'search'} onClick={() => console.log('GO TO SEARCH')} />}
                    {hasProfileButton && <IconBtn kind={'profile'} onClick={() => console.log('GO TO PROFILE')} />}
                    {hasLogoutBtn && <LogoutBtn />}
                    {hasBackToStartBtn && <BackToStartBtn />}
                </div>
            </div>
        </MuiAppBar>
    );
};
